/**
 * edge_back_controller.js — interactive edge-swipe-back (Stimulus).
 *
 * Mounted once on <body> (base.html, data-controller="... edge-back"). A drag
 * that starts within EDGE_PX of the left viewport edge scrubs the current page
 * to the right, 1:1 with the finger, over the previous page underneath; release
 * past the commit threshold settles the slide and calls history.back(), a
 * shorter drag scrubs everything back. This is the pop from motion.css
 * (mobile-push-*-back) driven by hand, so the two read as one motion.
 *
 * Layers: the live <body> is the outgoing page (translated, never cloned) and a
 * fixed .eb-underlay inserted BEFORE <body> is the incoming page: the previous
 * page's cached Turbo snapshot when there is one, otherwise its skeleton via the
 * shared builder in skeleton-nav.js. On commit the html gets data-nav-gesture,
 * so turbo-navigation.js stamps the restore visit kind none and Turbo restores
 * the page underneath with no second transition; cleanup runs on turbo:load.
 *
 * Ownership: the gesture only arms when the native layer does not own back,
 * read from ChippieNav.nativeOwnsBack() (turbo-navigation.js, which resolves
 * ChippieBridge.appInfo().ownsBack once per document and holds the default for
 * older shell builds). On Android gesture-navigation devices the OS edge
 * gesture usually pre-empts this one; that is fine, the automatic pop still
 * plays.
 *
 * Physics come from static/js/gestures.js; tuning constants stay here.
 */
import { Controller } from "@hotwired/stimulus";
import { bump, crossedThreshold, lockAxis, motionToken } from "../../gestures.js";

var EDGE_PX = 24;             // arm zone from the left viewport edge
var AXIS_DEADZONE = 8;        // px before committing to a horizontal/vertical axis
var COMMIT_FRACTION = 0.3;    // of viewport width...
var MIN_COMMIT_PX = 60;       // ...or this floor, whichever is larger
var MOBILE_MAX = 991.98;      // motion.css mobile push breakpoint
var UNDER_PARALLAX = 0.3;     // incoming page starts 30% off-left, dimmed (mirrors the pop keyframes)
var UNDER_DIM = 0.92;
var SAFETY_MS = 4000;         // strand guard if Turbo never restores

function settleMs() {
    var raw = motionToken('--dur-slow');
    var ms = parseFloat(raw);
    if (!isFinite(ms)) return 280;
    return /ms$/.test(raw) ? ms : ms * 1000;
}

export default class extends Controller {
    connect() {
        this.enabled = false;
        this.active = false;      // pointer down inside the edge zone
        this.tracking = false;    // axis locked horizontal, layers built
        this.committing = false;
        this.startX = 0;
        this.startY = 0;
        this.viewW = 0;
        this.d = 0;
        this.underlay = null;
        this._savedBg = null;

        this._onDown = this._down.bind(this);
        this._onMove = this._move.bind(this);
        this._onUp = this._up.bind(this);
        this.element.addEventListener('pointerdown', this._onDown, { passive: true });
        window.addEventListener('pointermove', this._onMove, { passive: false });
        window.addEventListener('pointerup', this._onUp);
        window.addEventListener('pointercancel', this._onUp);

        var self = this;
        if (window.ChippieNav && window.ChippieNav.nativeOwnsBack) {
            window.ChippieNav.nativeOwnsBack().then(function (owns) { self.enabled = !owns; });
        }
    }

    disconnect() {
        this.element.removeEventListener('pointerdown', this._onDown);
        window.removeEventListener('pointermove', this._onMove);
        window.removeEventListener('pointerup', this._onUp);
        window.removeEventListener('pointercancel', this._onUp);
        // A committing gesture must outlive this disconnect (Turbo swaps <body>)
        // so the underlay covers the restore; its own turbo:load handler cleans up.
        if (!this.committing) this._teardown();
        this.active = false;
        this.tracking = false;
    }

    _down(e) {
        if (!this.enabled || this.committing) return;
        if (window.innerWidth > MOBILE_MAX) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.clientX > EDGE_PX) return;
        if (window.history.length <= 1) return;
        if (document.body.classList.contains('modal-open')) return;
        if (document.documentElement.hasAttribute('data-nav-gesture')) return;
        var prev = window.ChippieNav && window.ChippieNav.previousLocation();
        if (!prev) return;
        try {
            if (new URL(prev, window.location.href).origin !== window.location.origin) return;
        } catch (_e) { return; }
        this.prev = prev;
        this.active = true;
        this.tracking = false;
        this.startX = e.clientX;
        this.startY = e.clientY;
    }

    _move(e) {
        if (!this.active) return;
        var dx = e.clientX - this.startX;
        var dy = e.clientY - this.startY;
        if (!this.tracking) {
            var axis = lockAxis(dx, dy, AXIS_DEADZONE);
            if (axis === null) return;
            if (axis === 'v' || dx < 0) { this.active = false; return; }   // scroll, or a leftward drag: not ours
            this._buildLayers();
            this.tracking = true;
        }
        if (e.cancelable) e.preventDefault();             // own the gesture; no native scroll
        this.d = Math.max(0, Math.min(this.viewW, dx));
        this._paint(this.d);
    }

    _up() {
        if (!this.active) return;
        this.active = false;
        if (!this.tracking) return;
        this.tracking = false;
        if (crossedThreshold(this.d, MIN_COMMIT_PX, COMMIT_FRACTION, this.viewW)) {
            this._commit();
        } else {
            this._cancel();
        }
    }

    // --- layers -------------------------------------------------------------

    _buildLayers() {
        this.viewW = window.innerWidth || 1;
        var underlay = document.createElement('div');
        underlay.className = 'eb-underlay';
        var page = document.createElement('div');
        page.className = 'eb-underlay__page';
        underlay.appendChild(page);

        var snapshot = this._cachedSnapshot(this.prev);
        if (snapshot) {
            var clone = snapshot.element.cloneNode(true);
            clone.querySelectorAll('script').forEach(function (s) { s.remove(); });
            // A page left by a nav tap was cached with that tap's instant
            // highlight; show it as the server rendered it.
            if (window.ChippieInstantNav) window.ChippieInstantNav.restore(clone);
            while (clone.firstChild) page.appendChild(clone.firstChild);
        } else if (window.ChippieSkeleton && window.ChippieSkeleton.buildSkeletonFor) {
            window.ChippieSkeleton.buildSkeletonFor(this.prev, page);
        }

        // The body's background normally propagates to the canvas, which would
        // paint UNDER the underlay; pin it on <html> and <body> so the body is
        // an opaque card sliding over the previous page.
        var bg = getComputedStyle(document.body).backgroundColor;
        this._savedBg = { html: document.documentElement.style.backgroundColor, body: document.body.style.backgroundColor };
        document.documentElement.style.backgroundColor = bg;
        document.body.style.backgroundColor = bg;
        document.body.style.transition = '';
        document.body.style.willChange = 'transform';

        document.documentElement.insertBefore(underlay, document.body);
        this.underlay = underlay;
        this._paint(0);
    }

    _cachedSnapshot(url) {
        try {
            var view = window.Turbo && window.Turbo.session && window.Turbo.session.view;
            return view ? view.getCachedSnapshotForLocation(new URL(url, window.location.href)) : null;
        } catch (_e) { return null; }
    }

    _paint(d) {
        var p = this.viewW ? d / this.viewW : 0;
        document.body.style.transform = 'translateX(' + d + 'px)';
        if (this.underlay) {
            this.underlay.style.transform = 'translateX(' + (-this.viewW * UNDER_PARALLAX * (1 - p)) + 'px)';
            this.underlay.style.filter = 'brightness(' + (UNDER_DIM + (1 - UNDER_DIM) * p) + ')';
        }
    }

    _settle(toD) {
        var ease = motionToken('--ease-settle') || 'ease-out';
        var ms = settleMs();
        var t = 'transform ' + ms + 'ms ' + ease;
        document.body.style.transition = t;
        if (this.underlay) this.underlay.style.transition = t + ', filter ' + ms + 'ms ' + ease;
        this._paint(toD);
        return ms;
    }

    _commit() {
        var self = this;
        this.committing = true;
        bump('light');
        var ms = this._settle(this.viewW);

        var done = false;
        var cleanup = function () {
            if (done) return;
            done = true;
            self._teardown();
            self.committing = false;
        };
        // document persists across the Turbo swap, so this fires after the
        // previous page has been restored underneath.
        document.addEventListener('turbo:load', cleanup, { once: true });
        setTimeout(cleanup, ms + SAFETY_MS);

        // Let the slide finish before the restore renders, so the restored body
        // lands exactly where the underlay already is.
        setTimeout(function () {
            document.documentElement.setAttribute('data-nav-gesture', '');
            window.history.back();
        }, ms);
    }

    _cancel() {
        var self = this;
        var ms = this._settle(0);
        setTimeout(function () { if (!self.tracking && !self.committing) self._teardown(); }, ms);
    }

    _teardown() {
        if (this.underlay && this.underlay.parentNode) this.underlay.remove();
        this.underlay = null;
        var body = document.body;
        if (body) {
            body.style.transition = '';
            body.style.transform = '';
            body.style.willChange = '';
            body.style.backgroundColor = this._savedBg ? this._savedBg.body : '';
        }
        document.documentElement.style.backgroundColor = this._savedBg ? this._savedBg.html : '';
        this._savedBg = null;
        this.d = 0;
    }
}
